import { PageBlock } from '@design-system/page-block';
import { PageHeader } from '@design-system/page-header-standard';
import { Tab, Tabs } from '@design-system/tabs';
import { useLocation } from '@reach/router';
import { navigate } from 'gatsby';
import React, { forwardRef, Suspense, useEffect, useState } from 'react';

import BodyRender from '~/components/BodyRender';
import { pageHeaderMapper } from '~/mappers/pageHeader';
import {
  generateLocationQueryString,
  getLocationQueryStringParam,
} from '~/utils/browser';

import { TabsBlockProps } from './models';

const TabsBlock = forwardRef<HTMLDivElement, TabsBlockProps>(
  ({ pageBlock, items, hasSpacing, componentId, currentPageUid }, ref) => {
    const [activeTabValue, setActiveTabValue] = useState(
      Number(getLocationQueryStringParam(componentId)) || 0
    );
    const location = useLocation();

    const handleTabChange = (newValue: number) => {
      setActiveTabValue(newValue);

      navigate(
        generateLocationQueryString({
          [componentId]: newValue.toString(),
        }),
        {
          state: { noScroll: true },
        }
      );
    };

    useEffect(() => {
      const activeTab = Number(getLocationQueryStringParam(componentId));

      setActiveTabValue(activeTab);
    }, [location.search, componentId]);

    return (
      <PageBlock
        {...pageBlock}
        className="tabs-block"
        ref={ref}
        id={componentId}
      >
        <Tabs
          value={activeTabValue}
          onChange={handleTabChange}
          {...{ hasSpacing }}
        >
          {items.map(({ label, ariaLabel, header, body, fields }) => (
            <Tab key={label} title={label} ariaLabel={ariaLabel || label}>
              {header?.headingBlock?.headline ? (
                <PageHeader
                  {...pageHeaderMapper({
                    pageHeader: { ...header, showBreadcrumbs: false },
                    fields,
                  })}
                />
              ) : null}
              <Suspense fallback="Loading...">
                <BodyRender items={body} currentPageUid={currentPageUid} />
              </Suspense>
            </Tab>
          ))}
        </Tabs>
      </PageBlock>
    );
  }
);

export default TabsBlock;
